import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Play, BookOpen, Trophy, Target, Gamepad2, Brain, Zap, Users, TrendingUp, Star, CheckCircle } from "lucide-react";

/**
 * HowItWorks Page Component
 * 
 * Comprehensive explanation of Centse's gamified learning methodology and platform features.
 * Demonstrates the step-by-step learning journey and the science behind our approach.
 * 
 * Key sections:
 * - Hero section with main value proposition
 * - Learning methodology explanation
 * - Step-by-step process walkthrough
 * - Gamification elements breakdown
 * - Success metrics and outcomes
 * - Getting started guide
 * 
 * @returns {JSX.Element} How It Works page component
 */
const HowItWorks = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
            <div className="text-2xl font-bold text-primary">Centse</div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-16">
        {/* Hero Section */}
        <section className="text-center space-y-6">
          <div className="space-y-4">
            <Badge variant="secondary" className="text-sm font-medium">
              Science-Backed Learning Experience
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              How Centse Makes
              <br />
              Learning Addictive
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              We've cracked the code on making financial education as engaging as your favorite game. 
              Here's exactly how our platform transforms boring lessons into exciting achievements.
            </p>
          </div>
        </section>

        {/* The Science Section */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              The Psychology of Addictive Learning
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our approach is built on proven psychological principles that make learning irresistible.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-primary/5 border-primary/20 hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Brain className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Dopamine-Driven Rewards</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-3">
                <p className="text-muted-foreground">
                  Every completed lesson triggers dopamine release through immediate rewards, 
                  creating positive associations with financial learning.
                </p>
                <Badge variant="outline" className="text-xs">
                  +75% Knowledge Retention
                </Badge>
              </CardContent>
            </Card>

            <Card className="bg-secondary/5 border-secondary/20 hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-6 w-6 text-secondary" />
                </div>
                <CardTitle className="text-xl">Progressive Mastery</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-3">
                <p className="text-muted-foreground">
                  Concepts build on each other systematically, ensuring mastery before advancement. 
                  No student left behind, no concept left unmastered.
                </p>
                <Badge variant="outline" className="text-xs">
                  +60% Completion Rate
                </Badge>
              </CardContent>
            </Card>

            <Card className="bg-accent/5 border-accent/20 hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-accent" />
                </div>
                <CardTitle className="text-xl">Social Motivation</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-3">
                <p className="text-muted-foreground">
                  Leaderboards, peer challenges, and collaborative goals leverage social psychology 
                  to maintain engagement and motivation.
                </p>
                <Badge variant="outline" className="text-xs">
                  +90% Engagement
                </Badge>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Step-by-Step Process */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Your Learning Journey
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From complete beginner to financial confidence in 6 progressive steps.
            </p>
          </div>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white font-bold text-xl">
                    1
                  </div>
                </div>
                <Card className="flex-1 bg-surface border-border">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Play className="h-6 w-6 text-primary" />
                      <CardTitle className="text-xl">Personalized Assessment</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Start with a fun, game-like assessment that identifies your current financial knowledge level. 
                      No judgment, just personalized starting points.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">5-minute quiz</Badge>
                      <Badge variant="secondary">Adaptive difficulty</Badge>
                      <Badge variant="secondary">Instant results</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center text-white font-bold text-xl">
                    2
                  </div>
                </div>
                <Card className="flex-1 bg-surface border-border">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-6 w-6 text-secondary" />
                      <CardTitle className="text-xl">Bite-Sized Learning Modules</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Learn through interactive modules designed like game levels. Each lesson is 5-10 minutes, 
                      perfect for busy schedules and optimal attention spans.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">Micro-learning</Badge>
                      <Badge variant="secondary">Interactive content</Badge>
                      <Badge variant="secondary">Real-world scenarios</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center text-white font-bold text-xl">
                    3
                  </div>
                </div>
                <Card className="flex-1 bg-surface border-border">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Gamepad2 className="h-6 w-6 text-accent" />
                      <CardTitle className="text-xl">Hands-On Practice Games</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Apply what you've learned through engaging mini-games. Budget simulations, 
                      investment challenges, and savings competitions make practice fun.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">Simulation games</Badge>
                      <Badge variant="secondary">Safe environment</Badge>
                      <Badge variant="secondary">Immediate feedback</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white font-bold text-xl">
                    4
                  </div>
                </div>
                <Card className="flex-1 bg-surface border-border">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Trophy className="h-6 w-6 text-primary" />
                      <CardTitle className="text-xl">Achievement & Progress Tracking</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Earn points, unlock badges, and climb leaderboards. Your progress is visualized 
                      in real-time, creating motivation to continue learning.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">XP points</Badge>
                      <Badge variant="secondary">Skill badges</Badge>
                      <Badge variant="secondary">Streak tracking</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Step 5 */}
            <div className="relative">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center text-white font-bold text-xl">
                    5
                  </div>
                </div>
                <Card className="flex-1 bg-surface border-border">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Users className="h-6 w-6 text-secondary" />
                      <CardTitle className="text-xl">Community Challenges</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Join weekly challenges with peers, share achievements, and learn from others. 
                      The social element keeps you accountable and motivated.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">Peer learning</Badge>
                      <Badge variant="secondary">Group challenges</Badge>
                      <Badge variant="secondary">Social accountability</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Step 6 */}
            <div className="relative">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center text-white font-bold text-xl">
                    6
                  </div>
                </div>
                <Card className="flex-1 bg-surface border-border">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-6 w-6 text-accent" />
                      <CardTitle className="text-xl">Real-World Application</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Graduate to real-world financial management with confidence. Track actual budgets, 
                      set savings goals, and make informed investment decisions.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">Personal finance tools</Badge>
                      <Badge variant="secondary">Goal tracking</Badge>
                      <Badge variant="secondary">Continued support</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Gamification Elements */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              What Makes It Addictive
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The secret sauce: game mechanics that trigger intrinsic motivation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-surface border-border text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Points & Rewards</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Earn XP for every lesson completed, quiz passed, and goal achieved. 
                  Immediate gratification drives continued engagement.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="h-6 w-6 text-secondary" />
                </div>
                <CardTitle className="text-lg">Achievement Badges</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Unlock special badges for milestones like "Budget Master" or "Investment Rookie." 
                  Collectible achievements create completion drive.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-6 w-6 text-accent" />
                </div>
                <CardTitle className="text-lg">Progress Levels</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Level up from "Cent Starter" to "Money Master." 
                  Clear progression paths provide direction and motivation.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Streak Challenges</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Maintain daily learning streaks for bonus points. 
                  The fear of breaking streaks creates powerful habit formation.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Proven Results
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our approach delivers measurable improvements in financial knowledge and behavior.
            </p>
          </div>

          <Card className="bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border-primary/20">
            <CardContent className="pt-8 pb-8">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-primary">0.313</div>
                  <div className="text-sm text-muted-foreground">Standard Deviation Improvement</div>
                  <p className="text-xs text-muted-foreground">
                    Statistically significant improvement in financial literacy scores vs. traditional methods
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-secondary">85%</div>
                  <div className="text-sm text-muted-foreground">User Retention Rate</div>
                  <p className="text-xs text-muted-foreground">
                    Students continue learning for 3+ months vs. 2 weeks with traditional courses
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-accent">92%</div>
                  <div className="text-sm text-muted-foreground">Positive Learning Experience</div>
                  <p className="text-xs text-muted-foreground">
                    Students report financial education as "fun" and "engaging" instead of boring or scary
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Getting Started */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Ready to Start Your Journey?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Join thousands of users who've transformed their relationship with money through our platform.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-surface border-border text-center">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Sign Up Free</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Create your account in under 30 seconds. 
                  No credit card required, just your commitment to learning.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border text-center">
              <CardHeader>
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="h-6 w-6 text-secondary" />
                </div>
                <CardTitle className="text-lg">Take Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Complete our fun 5-minute assessment to get your personalized learning path. 
                  No wrong answers, just the right starting point.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border text-center">
              <CardHeader>
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-6 w-6 text-accent" />
                </div>
                <CardTitle className="text-lg">Start Learning</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Begin your first lesson immediately. 
                  Watch your confidence grow as you master each financial concept.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate("/signup")}
                className="bg-gradient-to-r from-primary via-primary/90 to-primary hover:opacity-90 transition-all duration-300"
              >
                Start Learning Today
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/who-we-are")}
                className="border-border hover:bg-muted/50"
              >
                Learn About Our Mission
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HowItWorks;